<script>
import flatpickr from 'flatpickr'
import { Czech } from "flatpickr/dist/l10n/cs.js"
import { default as English } from "flatpickr/dist/l10n/default.js"
import { Polish } from "flatpickr/dist/l10n/pl.js"

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

import apiUsers from '@/api/api-users.js'
import apiOrders from '@/api/api-orders.js'
import apiFiles from "@/api/api-files.js"
import apiSamples from "@/api/api-samples.js"
import apiAnalyses from "@/api/api-analyses.js"
import apiFolders from "@/api/api-folders.js"

import MessageBoxQuestion from '../common/MessageBoxQuestion.vue'
import MessageBox from '../common/MessageBox.vue'
import SelectionForm from './SelectionForm.vue'

export default {
    name: 'OrderDetailComponent',

    components: {
        MessageBoxQuestion,
        MessageBox,
        SelectionForm
    },

    props: {
        userLanguage: {
            type: String,
            default: 'cz'
        },
        languageStrings: {
            type: Object,
            default: () => {}
        },
        userId: {
            type: Number,
            defaults: -1
        },
        orderId: {
            type: Number,
            defaults: -1
        }
    },

    emits: ['order-updated'],

    data() {
        return {
            fp: null,

            order: {
                'header': {
                    'id': 0,
                    'number': '',
                    'date': null,
                    'customer': {
                        'id': 0,
                        'name': ''
                    },
                    'locked': false,
                    'lockedDate': null,
                    'lockedPerson': null,
                    'invoiceNumber': '',
                    'invoiceDate': null,
                    'description': '',
                    'invoiceNumber2': '',
                    'noInvoice': false,
                    'invoiced': false
                },
                'analysis': [],
                'samples': []
            },
            orderPdfs: [],
            folders: [],

            messageBoxType: -1,
            showMessageBoxQuestion: false,
            messageBoxQuestionText: '',
            messageBoxText: '',
            showMessageBox: false,

            numberError: false,
            customerError: false,

            showSelectionForm: false,
            selectionFormTitle: '',
            selectionFormType: -1,
            selectionFormHeaders: [],
            selectionFormRows: []
        }
    },

    computed: {
        getAlsPrice() {
            return this.order.analysis.reduce((partialSum, a) => partialSum + a.price.alsPrice, 0) * this.order.samples.length
        },
        getKleentekPrice() {
            return this.order.analysis.reduce((partialSum, a) => partialSum + a.price.kleentekPrice, 0) * this.order.samples.length
        },
        selectedSamples() {
            return this.order.samples.map(s => s.sample.id)
        },
        selectedAnalyses() {
            return this.order.analysis.map(a => a.price.analysis.id)
        }
    },

    watch: {
        orderId: function() {
            this.loadOrder()
        }
    },

    async mounted() {
        if (this.userLanguage == 'pl') {
            flatpickr.localize(Polish)
        }
        else if (this.userLanguage == 'en') {
            English.firstDayOfWeek = 1
            English.time_24hr = true
            flatpickr.localize(English)
        }
        else {
            flatpickr.localize(Czech)
        }

        this.fp = flatpickr('#invoiceDate',
        {
            enableTime: false,
            dateFormat: 'Y-m-dTH:i:S',
            altInput: true,
            altFormat: 'j.n.Y',
            disableMobile: true
        })

        let result = await apiUsers.getFlatFolders(this.userId)
        if (result.code === 0) {
            let customers = result.data.slice().sort((a, b) => a.name.localeCompare(b.name))
            this.folders = customers
        }
            
        this.loadOrder()
    },

    methods: {
        // Helpers
        stopEnterPropagation() {
            // dummy to stop click propagation upwards in hierarchy            
        },
        formatDate(date) {
            return date !== null ? dayjs(date + 'Z').utcOffset(0).format('D.M.YYYY') : ''
        },

        async loadOrder() {
            this.order = {
                'header': {
                    'id': 0,
                    'number': '',
                    'date': null,
                    'customer': {
                        'id': 0,
                        'name': ''
                    },
                    'locked': false,
                    'lockedDate': null,
                    'lockedPerson': null,
                    'invoiceNumber': '',
                    'invoiceDate': null,
                    'description': '',
                    'invoiceNumber2': '',
                    'noInvoice': false,
                    'invoiced': false
                },
                'analysis': [],
                'samples': []
            }
            this.orderPdfs = []
            this.fp.clear()

            if (this.orderId !== -1) {
                let result = await apiOrders.getOrder(this.orderId)
                if (result.code === 0) {
                    if (result.data.header.invoiceDate != null) result.data.header.invoiceDate = dayjs(result.data.header.invoiceDate + 'Z').utcOffset(0).format('YYYY-MM-DDTHH:mm:00')
                    this.order = result.data
                    if (this.order.header.invoiceDate != null) { this.fp.setDate(new Date(this.order.header.invoiceDate)) } else { this.fp.clear() }
                }

                for (let i = 0; i < this.order.samples.length; i++) {
                    result = await apiSamples.getSample(this.order.samples[i].sample.id)
                    if (result.code === 0) {
                        this.order.samples[i].sample = result.data
                    }
                }

                result = await apiOrders.getOrderPdfs(this.orderId)
                if (result.code === 0) {
                    this.orderPdfs = result.data
                }
            }
            else {
                let result = await apiOrders.getOrders(false, false, false)
                if (result.code === 0 && result.data.length > 0) {
                    let lastNum = result.data.sort((a, b) => b.date - a.date)[0].header.number
                    if (lastNum.length > 4) {
                        this.order.header.number = lastNum.substring(0, lastNum.length - 4) + (Number(lastNum.substring(lastNum.length - 4)) + 1).toString().padStart(4, '0')
                    }
                }
            }
        },
        invoiced() {
            if (this.order.header.invoiced) {
                this.fp.setDate(new Date())
            }
        },
        async downloadPdf(type) {
            let pdf = this.orderPdfs.find(p => p.type === type)
            let response = await apiFiles.getFileContentBlob(pdf.dataUrl)
            if (response.code === 0) {
                const blob = new Blob([response.data.data], { type: response.data.headers['content-type'] })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(blob)
                link.target = '_blank'
                link.download = pdf.name
                link.click()
                URL.revokeObjectURL(link.href)
            }
        },
        async unlockOrder() {
            this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order062 != null ? this.languageStrings.ui.order062 : 'Odemknout objednávku?'
            this.messageBoxType = 1
            this.showMessageBoxQuestion = true
        },
        async saveOrder() {
            if (this.order.header.number.length === 0) {
                this.numberError = true
                return
            }
            else {
                this.numberError = false
            }
            if (this.order.header.customer.id === 0) {
                this.customerError = true
                return
            }
            else {
                this.customerError = false
            }

            if (this.order.header.date == null || this.order.header.date === '') this.order.header.date = dayjs().utcOffset(0).format('YYYY-MM-DDTHH:mm:00')
            if (this.order.header.lockedDate === '') this.order.header.lockedDate = null
            if (this.order.header.invoiceDate === '') this.order.header.invoiceDate = null

            if (this.order.header.id === 0) { // new
                let result = await apiOrders.postOrder(this.order)
                if (result.code === 0) {
                    this.order = result.data
                    this.$emit('order-updated', this.order.header.id)
                }
            }
            else { // edit
                let result = await apiOrders.putOrder(this.order.header.id, this.order)
                if (result.code === 0) {
                    this.order = result.data
                    this.$emit('order-updated', this.order.header.id)
                }
            }
        },
        async generateOrder() {
            if (this.order.header.number.length === 0) {
                this.numberError = true
                return
            }
            else {
                this.numberError = false
            }
            if (this.order.header.customer.id === 0) {
                this.customerError = true
                return
            }
            else {
                this.customerError = false
            }

            this.messageBoxText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order043 != null ? this.languageStrings.ui.order043 : 'Generování objednávky...'
            this.showMessageBox = true
            await apiOrders.createPdf(this.order.header.id)
            await apiOrders.createProtocol(this.order.header.id)
            this.showMessageBox = false
            let result = await apiOrders.getOrderPdfs(this.orderId)
            if (result.code === 0) {
                this.orderPdfs = result.data
                return true
            }
        },
        sendOrder() {
            this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order063 != null ? this.languageStrings.ui.order063 : 'Odeslat objednávku?'
            this.messageBoxType = 2
            this.showMessageBoxQuestion = true
        },
        deleteOrder() {
            this.messageBoxQuestionText = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order064 != null ? this.languageStrings.ui.order064 : 'Smazat objednávku?'
            this.messageBoxType = 3
            this.showMessageBoxQuestion = true
        },
        async selectSamples() {
            this.selectionFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order056 != null ? this.languageStrings.ui.order056 : 'Výběr vzorků'
            this.selectionFormType = 1

            this.selectionFormHeaders = [
                {
                    id: 'number-als',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order050 != null ? this.languageStrings.ui.order050 : 'Č. vzorku ALS',
                    sort: true
                },
                {
                    id: 'machine-name',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order051 != null ? this.languageStrings.ui.order051 : 'Název stroje',
                    sort: true
                },
                {
                    id: 'machine-id',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order052 != null ? this.languageStrings.ui.order052 : 'ID stroje',
                    sort: true
                }
            ]

            this.selectionFormRows = []
            if (this.order.header.customer.id < 1) return

            let response = await apiFolders.getSamples(this.order.header.customer.id)
            if (response.code === 0) {
                let s = response.data.filter(a => (this.selectedSamples.includes(a.id) || (a.receivingDate == null && !a.locked)) && !a.alsNumber.startsWith("9"))
                for (let i = 0; i < s.length; i++) {
                    if (this.selectionFormRows.some(r => r.id === s[i].id)) continue

                    this.selectionFormRows.push({
                        id: s[i].id,
                        selected: this.selectedSamples.includes(s[i].id),
                        cells: [
                            {
                                id: 'number-als',
                                contenttype: 'text',
                                content: s[i].alsNumber
                            },
                            {
                                id: 'machine-name',
                                contenttype: 'text',
                                content: s[i].machine.name
                            },
                            {
                                id: 'machine-id',
                                contenttype: 'text',
                                content: !s[i].machine.internalKey.startsWith('Machine') ? s[i].machine.internalKey : ''
                            }
                        ]
                    })
                }
            }

            this.showSelectionForm = true
        },
        async selectAnalyses() {
            this.selectionFormTitle = this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order059 != null ? this.languageStrings.ui.order059 : 'Výběr analýz'
            this.selectionFormType = 2

            this.selectionFormHeaders = [
                {
                    id: 'analysis',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order058 != null ? this.languageStrings.ui.order058 : 'Analýza',
                    sort: true
                },
                {
                    id: 'id-als',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order060 != null ? this.languageStrings.ui.order060 : 'ID ALS',
                    sort: true
                },
                {
                    id: 'group',
                    name: this.languageStrings != null && this.languageStrings.ui != null && this.languageStrings.ui.order061 != null ? this.languageStrings.ui.order061 : 'Skupina',
                    sort: true
                }
            ]

            this.selectionFormRows = []
            let response = await apiAnalyses.getPrices()
            if (response.code === 0) {
                let a = response.data.filter(a => a.analysis.pointer != null && a.alsPrice != null && a.alsPrice > 0).sort((a, b) => a.analysis.analasisType.id - b.analysis.analasisType.id || (a.analysis.pointer == null ? -1 : b.analysis.pointer == null ? 1 : a.analysis.pointer.localeCompare(b.analysis.pointer)))
                for (let i = 0; i < a.length; i++) {
                    this.selectionFormRows.push({
                        id: a[i].analysis.id,
                        selected: this.selectedAnalyses.includes(a[i].analysis.id),
                        cells: [
                            {
                                id: 'analysis',
                                contenttype: 'text',
                                content: a[i].analysis.pointer
                            },
                            {
                                id: 'id-als',
                                contenttype: 'text',
                                content: a[i].analysis.idAls
                            },
                            {
                                id: 'group',
                                contenttype: 'text',
                                content: this.languageStrings != null && this.languageStrings.ui != null ? this.languageStrings.ui[a[i].analysis.analasisType.name] : a[i].analysis.analasisType.name
                            }
                        ]
                    })
                }
            }

            this.showSelectionForm = true
        },
        async selectionFormClosed(type, data) {
            this.showSelectionForm = false

            let selected = data.filter(d => d.selected)
            switch (type) {
                case 1: // Samples
                    this.order.samples = this.order.samples.filter(a => selected.some(b => b.id === a.sample.id))

                    for (let i = 0; i < selected.length; i++) {
                        if (this.order.samples.some(s => s.sample.id === selected[i].id)) continue

                        let response = await apiSamples.getSample(selected[i].id)
                        if (response.code === 0) {
                            this.order.samples.push({ 'orderId': this.order.id, 'sample': response.data, 'oilBrand': response.data.oilBrand, 'collectionDate': response.data.collectionDate, 'reference': false, 'note': '' })
                        }
                    }
                    break

                case 2: // Analyzes
                    this.order.analysis = []
                    for (let i = 0; i < selected.length; i++) {
                        let response = await apiAnalyses.getPrice(selected[i].id)
                        if (response.code === 0) {
                            this.order.analysis.push({ 'orderId': this.order.id, 'price': response.data })
                        }
                    }
                    break
            }
        },

        // Message boxes
        async messageBoxQuestionClosed(result, type) {            
            this.showMessageBoxQuestion = false

            if (result !== 0) {
                return
            }

            let res = { code: 1, data: null }
            
            switch (type) {
                case 1: { // Unlock
                    res = await apiOrders.unlockOrder(this.order.header.id)
                    if (res.code === 0) {
                        this.order.header.locked = false
                    }
                }
                break

                case 2: { // Send
                    let result = await this.generateOrder()
                    if (result) {
                        res = await apiOrders.sendOrder(this.order.header.id)
                        if (res.code === 0) {
                            res = await apiOrders.lockOrder(this.order.header.id)
                            if (res.code === 0) {
                                this.order.header.locked = true
                            }
                        }
                    }
                }
                break

                case 3: { // Delete
                    res = await apiOrders.deleteOrder(this.order.header.id)
                    if (res.code === 0) {
                        this.$emit('order-updated', -1)
                    }
                }
                break
            }
        },
    }
}
</script>

<template>
    <div class="rows">
        <div class="order-detail-top">
            <table>
                <tr>
                    <td>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order036 != null ? languageStrings.ui.order036 : 'Č. objednávky' }}</td>
                    <td>
                        <input type="text" :class="numberError ? 'error' : ''" v-model="order.header.number" autocomplete="off" :disabled="order.header.locked != null && order.header.locked ? true : false" />
                        <span class="color-red">&nbsp;*</span>
                    </td>
                    <td><button class="button-small" @click="generateOrder" :disabled="order.header.locked != null && order.header.locked ? true : false">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order039 != null ? languageStrings.ui.order039 : 'Generovat objednávku' }}</button></td>
                    <td><button class="button-small" @click="sendOrder" :disabled="order.header.locked != null && order.header.locked ? true : false">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order040 != null ? languageStrings.ui.order040 : 'Odeslat' }}</button></td>
                    <td>&nbsp;</td>
                    <td colspan="2">
                        <span v-if="orderPdfs.length > 0 && orderPdfs.findIndex(p => p.type === 0) >= 0">
                            <img src="@/assets/images/download.svg" @click="downloadPdf(0)" height="15px" style="cursor: pointer; margin-right: 5px;">
                            <span>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order044 != null ? languageStrings.ui.order044 : 'Objednávka' }}</span>
                        </span>
                    </td>
                </tr>
                <tr>
                    <td>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order029 != null ? languageStrings.ui.order029 : 'Zákazník' }}</td>
                    <td>
                        <select :class="customerError ? 'error' : ''" v-model="order.header.customer.id" :disabled="order.header.locked != null && order.header.locked ? true : false">
                            <option v-for="f in folders" :key="f.id" :value="f.id">{{ f.name }}</option>
                        </select>
                        <span class="color-red">&nbsp;*</span>
                    </td>
                    <td style="text-align: right;"><button class="button-small" @click="deleteOrder" :disabled="order.header.locked != null && order.header.locked ? true : false">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order041 != null ? languageStrings.ui.order041 : 'Smazat' }}</button></td>
                    <td style="text-align: right;"><button class="button-small" @click="saveOrder">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order042 != null ? languageStrings.ui.order042 : 'Uložit' }}</button></td>
                    <td></td>
                    <td colspan="2">
                        <span v-if="orderPdfs.length > 0 && orderPdfs.findIndex(p => p.type === 1) >= 0">
                            <img src="@/assets/images/download.svg" @click="downloadPdf(1)" height="15px" style="cursor: pointer; margin-right: 5px;">
                            <span>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order014 != null ? languageStrings.ui.order014 : 'Předávací protokol' }}</span>
                        </span>
                    </td>
                </tr>
                <tr>
                    <td>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order037 != null ? languageStrings.ui.order037 : 'Č. faktury' }}</td>
                    <td>
                        <input type="text" v-model="order.header.invoiceNumber" autocomplete="off" />
                    </td>
                    <td colspan="2" style="text-align: right;"><button v-if="order.header.locked != null && order.header.locked" class="button-small" @click="unlockOrder">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order065 != null ? languageStrings.ui.order065 : 'Odemknout objednávku' }}</button></td>
                    <td></td>
                    <td></td>
                    <td></td>
                </tr>
                <tr>
                    <td>{{ (languageStrings != null && languageStrings.ui != null && languageStrings.ui.order037 != null ? languageStrings.ui.order037 : 'Č. faktury') + ' 2' }}</td>
                    <td>
                        <input type="text" v-model="order.header.invoiceNumber2" autocomplete="off" />
                    </td>
                    <td><input type="checkbox" v-model="order.header.noInvoice" />{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order045 != null ? languageStrings.ui.order045 : 'Nefakturovat' }}</td>
                    <td></td>
                    <td></td>
                    <td>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order047 != null ? languageStrings.ui.order047 : 'Cena ALS [Kč]' }}</td>
                    <td><input type="text" class="price" :value="getAlsPrice" disabled /></td>
                </tr>
                <tr>
                    <td>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order066 != null ? languageStrings.ui.order066 : 'Datum fakturace' }}</td>
                    <td>
                        <input type="text" id="invoiceDate" v-model="order.header.invoiceDate" autocomplete="off" />
                    </td>
                    <td><input type="checkbox" v-model="order.header.invoiced" @change="invoiced" />{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order046 != null ? languageStrings.ui.order046 : 'Vyfakturováno' }}</td>
                    <td></td>
                    <td></td>
                    <td>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order048 != null ? languageStrings.ui.order048 : 'Cena KLEENTEK [Kč]' }}</td>
                    <td><input type="text" class="price" :value="getKleentekPrice" disabled /></td>
                </tr>
                <tr>
                    <td colspan="7">
                        <div>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order038 != null ? languageStrings.ui.order038 : 'Poznámka' }}</div>
                        <textarea v-model="order.header.description" @keydown.stop="stopEnterPropagation"></textarea>
                    </td>
                </tr>
            </table>
        </div>
        
        <div class="order-detail-middle">
            <button class="button-small" @click="selectSamples" :disabled="order.header.locked != null && order.header.locked ? true : false">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order049 != null ? languageStrings.ui.order049 : 'Vybrat vzorky' }}</button>
            <table>
                <tr>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order050 != null ? languageStrings.ui.order050 : 'Č. vzorku ALS' }}</th>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order051 != null ? languageStrings.ui.order051 : 'Název stroje' }}</th>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order052 != null ? languageStrings.ui.order052 : 'ID stroje' }}</th>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order053 != null ? languageStrings.ui.order053 : 'Typ oleje' }}</th>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order054 != null ? languageStrings.ui.order054 : 'Datum odběru' }}</th>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order055 != null ? languageStrings.ui.order055 : 'Referenční' }}</th>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order038 != null ? languageStrings.ui.order038 : 'Poznámka' }}</th>
                </tr>
                <tr v-for="(s, i) in order.samples" :key="i">
                    <td>{{ s.sample.sif }}</td>
                    <td>{{ s.sample.machine != null ? s.sample.machine.name : '' }}</td>
                    <td>{{ s.sample.machine != null ? !s.sample.machine.internalKey.startsWith('Machine') ? s.sample.machine.internalKey : '' : '' }}</td>
                    <td>{{ s.oilBrand }}</td>
                    <td>{{ formatDate(s.collectionDate) }}</td>
                    <td><input type="checkbox" v-model="s.reference" :disabled="order.header.locked != null && order.header.locked ? true : false" /></td>
                    <td><input type="text" v-model="s.note" autocomplete="off" :disabled="order.header.locked != null && order.header.locked ? true : false" /></td>
                </tr>
            </table>
        </div>
        
        <div class="order-detail-bottom">
            <button class="button-small" @click="selectAnalyses" :disabled="order.header.locked != null && order.header.locked ? true : false">{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order057 != null ? languageStrings.ui.order057 : 'Vybrat analýzy' }}</button>
            <table>
                <tr>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order058 != null ? languageStrings.ui.order058 : 'Analýza' }}</th>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order047 != null ? languageStrings.ui.order047 : 'Cena ALS [Kč]' }}</th>
                    <th>{{ languageStrings != null && languageStrings.ui != null && languageStrings.ui.order048 != null ? languageStrings.ui.order048 : 'Cena KLEENTEK [Kč]' }}</th>
                </tr>
                <tr v-for="(a, i) in order.analysis" :key="i">
                    <td>{{ a.price.analysis != null ? a.price.analysis.pointer : "???" }}</td>
                    <td>{{ a.price.alsPrice }}</td>
                    <td>{{ a.price.kleentekPrice }}</td>
                </tr>
            </table>
        </div>
    </div>

    <SelectionForm v-if="showSelectionForm" @form-closed="selectionFormClosed" :title="selectionFormTitle" :type="selectionFormType" :headers="selectionFormHeaders" :rows="selectionFormRows"></SelectionForm>

    <!-- Message box - Question -->
    <MessageBoxQuestion v-if="showMessageBoxQuestion" :text="messageBoxQuestionText" @form-closed="messageBoxQuestionClosed" :type="messageBoxType"
        :yesButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form003 != null ? languageStrings.ui.form003 : 'Ano'"
        :noButtonText="languageStrings != null && languageStrings.ui != null && languageStrings.ui.form004 != null ? languageStrings.ui.form004 : 'Ne'"
    />
    <!-- Message box - Question -->

    <!-- Message box -->
    <MessageBox v-if="showMessageBox" :text="messageBoxText" :show-buttons="false" />
    <!-- Message box -->
</template>